import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { getAuthToken } from '../../services/authService';
import {
  analyzeRecipeCorrection,
  applyRecipeCorrection,
  fetchRecipeCorrectionPrompt,
  fetchRecipeCorrectionSession,
  fetchRecipeCorrectionStatus,
  importAnalyzeRecipeCorrection,
  fetchLatestRecipeCorrectionSnapshot,
  saveRecipeCorrectionPrompt,
} from './api';
import { OriginalDiffText, ProposedDiffText } from './DiffText';
import PromptRichEditor from './PromptRichEditor';
import ProposedField from './ProposedField';
import { markdownBoldToHtml, normalizeProposedRecipeFieldValues, normalizeRecipeFieldValues } from './recipeTextFormat';
import {
  RecipeCorrectionAnalysis,
  RecipeCorrectionPrompt,
  RecipeCorrectionSession,
  RecipeCorrectionSnapshot,
  RecipeFieldValues,
  ScalarKey,
} from './types';

interface RecipeCorrectionPageProps {
  assetId: string;
  openedFromFullPage: boolean;
  readOnlySnapshot?: boolean;
  onClose: () => void;
  onApplied: (assetId: string) => void;
}

const NAVY = '#072f53';
const CHECK_GREEN = '#18864a';
const PAGE_BG = '#eef2f6';

const SCALAR_FIELDS: Array<{ key: ScalarKey; label: string; multiline?: boolean }> = [
  { key: 'receptnamn', label: 'Receptnamn' },
  { key: 'beskrivning', label: 'Beskrivning', multiline: true },
  { key: 'tillagningstid', label: 'Tillagningstid' },
  { key: 'antalPortioner', label: 'Antal portioner' },
];

function compactList(values: string[] | undefined): string[] {
  const trimmed = (values || []).map((value) => String(value || '').trim());
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') trimmed.pop();
  return trimmed;
}

function thumbnailSrc(assetId: string): string {
  const token = getAuthToken();
  return `/api/assets/${assetId}/preview/thumbnail${token ? `?token=${encodeURIComponent(token)}` : ''}`;
}

const greenCheckboxSx = {
  color: '#cbd5e1',
  p: 0.5,
  '&.Mui-checked': { color: CHECK_GREEN },
  '&.Mui-disabled': { color: '#e2e8f0' },
  '& .MuiSvgIcon-root': { fontSize: 28 },
};

function ApproveCheck(props: {
  checked: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  const title = props.readOnly
    ? (props.checked ? 'Spartes vid godkännandet' : 'Spartes inte')
    : props.disabled
      ? 'Ingen ändring att spara'
      : (props.checked ? 'Sparas vid godkännande' : 'Sparas inte – originalet behålls');
  return (
    <Tooltip title={title}>
      <span>
        <Checkbox
          checked={props.checked}
          disabled={props.disabled}
          onChange={(event) => props.onChange(event.target.checked)}
          icon={<RadioButtonUncheckedIcon />}
          checkedIcon={<CheckCircleIcon />}
          inputProps={{ 'aria-label': props.label }}
          sx={greenCheckboxSx}
        />
      </span>
    </Tooltip>
  );
}

function ColumnLabel({ children, tone }: { children: React.ReactNode; tone: 'original' | 'proposed' }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        mb: 0.75,
        color: tone === 'original' ? '#b42318' : CHECK_GREEN,
      }}
    >
      {children}
    </Typography>
  );
}

function ReviewCard({
  accepted,
  changed,
  children,
}: {
  accepted: boolean;
  changed: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '44px 1fr 1fr' },
        gap: { xs: 1, md: 1.5 },
        alignItems: 'start',
        mb: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: accepted
          ? 'rgba(24,134,74,0.35)'
          : changed
            ? 'rgba(7,47,83,0.12)'
            : 'rgba(7,47,83,0.06)',
        bgcolor: accepted ? 'rgba(24,134,74,0.06)' : '#fff',
        boxShadow: accepted ? '0 1px 2px rgba(24,134,74,0.08)' : 'none',
      }}
    >
      {children}
    </Box>
  );
}

const RecipeCorrectionPage: React.FC<RecipeCorrectionPageProps> = ({
  assetId,
  openedFromFullPage,
  readOnlySnapshot = false,
  onClose,
  onApplied,
}) => {
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openaiAvailable, setOpenaiAvailable] = useState(true);
  const [session, setSession] = useState<RecipeCorrectionSession | null>(null);
  const [prompt, setPrompt] = useState<RecipeCorrectionPrompt | null>(null);
  const [promptDraft, setPromptDraft] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [analysis, setAnalysis] = useState<RecipeCorrectionAnalysis | null>(null);
  const [proposed, setProposed] = useState<RecipeFieldValues | null>(null);
  const [acceptedScalars, setAcceptedScalars] = useState<Set<ScalarKey>>(new Set());
  const [acceptedIngredients, setAcceptedIngredients] = useState<Set<number>>(new Set());
  const [acceptedSteps, setAcceptedSteps] = useState<Set<number>>(new Set());
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [snapshotMeta, setSnapshotMeta] = useState<Pick<RecipeCorrectionSnapshot, 'approvedByName' | 'appliedAt'> | null>(null);

  const applyAnalysis = (next: RecipeCorrectionAnalysis) => {
    const original = normalizeRecipeFieldValues(next.original);
    const proposedValues = normalizeProposedRecipeFieldValues(next.proposed);
    setAnalysis({
      ...next,
      original,
      proposed: proposedValues,
      changes: next.changes.map((change) => ({
        ...change,
        fore: markdownBoldToHtml(change.fore),
        efter: markdownBoldToHtml(change.efter),
      })),
    });
    setProposed({
      ...proposedValues,
      ingredienser: [...proposedValues.ingredienser],
      instruktioner: [...proposedValues.instruktioner],
    });
    setAcceptedScalars(new Set(next.defaultAcceptedScalars));
    setAcceptedIngredients(new Set(next.defaultAcceptedIngredientIndexes.filter((index) => index < proposedValues.ingredienser.length)));
    setAcceptedSteps(new Set(next.defaultAcceptedStepIndexes.filter((index) => index < proposedValues.instruktioner.length)));
    setEditingKey(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (readOnlySnapshot) {
        const [nextSession, snapshot] = await Promise.all([
          fetchRecipeCorrectionSession(assetId),
          fetchLatestRecipeCorrectionSnapshot(assetId),
        ]);
        if (!snapshot) {
          throw new Error('Ingen sparad receptändring hittades.');
        }
        setSession({ ...nextSession, canEditPrompt: false });
        setSnapshotMeta({ approvedByName: snapshot.approvedByName, appliedAt: snapshot.appliedAt });
        setPrompt({
          systemPrompt: snapshot.promptSnapshot || '',
          model: '',
          updatedAt: snapshot.appliedAt,
        });
        setPromptDraft(snapshot.promptSnapshot || '');
        setOpenaiAvailable(true);
        applyAnalysis({
          mode: snapshot.mode,
          original: snapshot.original,
          proposed: snapshot.proposed,
          changes: snapshot.changes || [],
          overflowWarnings: [],
          defaultAcceptedScalars: snapshot.acceptedScalars || [],
          defaultAcceptedIngredientIndexes: snapshot.acceptedIngredientIndexes || [],
          defaultAcceptedStepIndexes: snapshot.acceptedStepIndexes || [],
          sourceFileName: snapshot.sourceFileName,
        });
        return;
      }

      const [nextSession, nextPrompt, status] = await Promise.all([
        fetchRecipeCorrectionSession(assetId),
        fetchRecipeCorrectionPrompt(),
        fetchRecipeCorrectionStatus().catch(() => ({ available: false, openai: false })),
      ]);
      setSession(nextSession);
      setPrompt(nextPrompt);
      setPromptDraft(nextPrompt.systemPrompt || '');
      setOpenaiAvailable(status.openai !== false);
    } catch (err: any) {
      setError(err?.message || 'Kunde inte ladda receptkorrigeringen.');
    } finally {
      setLoading(false);
    }
  }, [assetId, readOnlySnapshot]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAnalyze = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      applyAnalysis(await analyzeRecipeCorrection(assetId));
    } catch (err: any) {
      setError(err?.message || 'Analysen misslyckades.');
    } finally {
      setAnalyzing(false);
    }
  };

  const runImport = async (file: File) => {
    setAnalyzing(true);
    setError(null);
    setImportConfirmOpen(false);
    setPendingFile(null);
    try {
      applyAnalysis(await importAnalyzeRecipeCorrection(assetId, file));
    } catch (err: any) {
      setError(err?.message || 'Importen misslyckades.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileChosen = (file: File | null) => {
    if (!file) return;
    if (session && !session.emptyRecipe) {
      setPendingFile(file);
      setImportConfirmOpen(true);
      return;
    }
    void runImport(file);
  };

  const handleSavePrompt = async () => {
    setSavingPrompt(true);
    setError(null);
    try {
      const saved = await saveRecipeCorrectionPrompt(promptDraft);
      setPrompt(saved);
      setPromptDraft(saved.systemPrompt || '');
    } catch (err: any) {
      setError(err?.message || 'Kunde inte spara masterprompten.');
    } finally {
      setSavingPrompt(false);
    }
  };

  const updateProposedScalar = (key: ScalarKey, value: string) => {
    setProposed((current) => current ? { ...current, [key]: value } : current);
    const originalValue = String(analysis?.original[key] || '').trim();
    if (value.trim() !== originalValue) {
      setAcceptedScalars((current) => new Set(current).add(key));
    }
  };

  const updateProposedList = (kind: 'ingredienser' | 'instruktioner', index: number, value: string) => {
    setProposed((current) => {
      if (!current) return current;
      const next = [...current[kind]];
      next[index] = value;
      return { ...current, [kind]: next };
    });
    const originalValue = String((kind === 'ingredienser' ? analysis?.original.ingredienser : analysis?.original.instruktioner)?.[index] || '').trim();
    if (value.trim() !== originalValue) {
      if (kind === 'ingredienser') {
        setAcceptedIngredients((current) => new Set(current).add(index));
      } else {
        setAcceptedSteps((current) => new Set(current).add(index));
      }
    }
  };

  const leftoverIngredients = useMemo(() => {
    if (!analysis || !proposed) return [] as Array<{ index: number; original: string }>;
    const originalFilled = compactList(analysis.original.ingredienser);
    const nextLength = compactList(proposed.ingredienser).length;
    return originalFilled
      .map((value, index) => ({ index, original: value }))
      .filter((entry) => entry.index >= nextLength);
  }, [analysis, proposed]);

  const leftoverSteps = useMemo(() => {
    if (!analysis || !proposed) return [] as Array<{ index: number; original: string }>;
    const originalFilled = compactList(analysis.original.instruktioner);
    const nextLength = compactList(proposed.instruktioner).length;
    return originalFilled
      .map((value, index) => ({ index, original: value }))
      .filter((entry) => entry.index >= nextLength);
  }, [analysis, proposed]);

  const masterPromptReady = Boolean((prompt?.systemPrompt || '').trim());
  const canRun = !analyzing && openaiAvailable && masterPromptReady;
  const canApprove = Boolean(proposed && analysis && !analyzing && !saving && !readOnlySnapshot);
  const acceptedCount = acceptedScalars.size + acceptedIngredients.size + acceptedSteps.size;
  const leftoverCount = leftoverIngredients.length + leftoverSteps.length;

  const handleApprove = async () => {
    if (!proposed || !analysis) return;
    setSaving(true);
    setError(null);
    try {
      await applyRecipeCorrection({
        assetId,
        mode: analysis.mode,
        proposed,
        acceptedScalars: Array.from(acceptedScalars),
        acceptedIngredientIndexes: Array.from(acceptedIngredients),
        acceptedStepIndexes: Array.from(acceptedSteps),
        changes: analysis.changes,
        sourceFileName: analysis.sourceFileName,
      });
      onApplied(assetId);
    } catch (err: any) {
      setError(err?.message || 'Kunde inte spara receptet.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, bgcolor: PAGE_BG }}>
        <CircularProgress />
        <Typography>Laddar receptkorrigering...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', bgcolor: PAGE_BG }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 1.5,
            mb: 2,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
            <Tooltip title={openedFromFullPage ? 'Tillbaka till receptet' : 'Tillbaka'}>
              <IconButton
                onClick={onClose}
                aria-label="Tillbaka"
                sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'rgba(7,47,83,0.12)', flexShrink: 0 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            {session?.assetId && (
              <Box
                component="img"
                src={thumbnailSrc(session.assetId)}
                alt=""
                sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, bgcolor: 'white', boxShadow: '0 1px 4px rgba(7,47,83,0.12)' }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: NAVY, fontWeight: 700, letterSpacing: 1.1, lineHeight: 1 }}>
                {readOnlySnapshot ? 'Sparad receptändring' : 'Receptkorrigering'}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY, lineHeight: 1.2 }}>
                {session?.title || 'Recept'}
              </Typography>
              {readOnlySnapshot && snapshotMeta && (
                <Typography variant="body2" sx={{ color: '#475569', mt: 0.25 }}>
                  Godkänd av {snapshotMeta.approvedByName}, {new Date(snapshotMeta.appliedAt).toLocaleString('sv-SE')} · skrivskyddad visning
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' }, alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {!readOnlySnapshot && (
              <>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={!canRun}
                  startIcon={<UploadFileIcon />}
                  sx={{ textTransform: 'none', fontWeight: 700, borderColor: NAVY, color: NAVY, bgcolor: '#fff', px: 2 }}
                >
                  Importera nytt recept
                  <input
                    type="file"
                    hidden
                    accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(event) => {
                      handleFileChosen(event.target.files?.[0] || null);
                      event.target.value = '';
                    }}
                  />
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void runAnalyze()}
                  disabled={!canRun}
                  startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: NAVY, px: 2.25, '&:hover': { bgcolor: '#0a4175' } }}
                >
                  {analyzing ? 'Förbättrar...' : 'Förbättra recept'}
                </Button>
              </>
            )}
            <Tooltip title="Masterprompt">
              <IconButton
                onClick={() => setPromptOpen(true)}
                aria-label="Masterprompt"
                sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'rgba(7,47,83,0.12)' }}
              >
                <SettingsOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!readOnlySnapshot && !openaiAvailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>OpenAI är inte tillgänglig. Analysen kan inte köras förrän nyckeln är konfigurerad.</Alert>
        )}
        {!readOnlySnapshot && !masterPromptReady && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Masterprompten är tom. En administratör måste spara reglerna innan analys eller import kan köras.
          </Alert>
        )}
        {!readOnlySnapshot && session?.emptyRecipe && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Receptfälten är tomma. Importera ett Word- eller PDF-recept, eller förbättra receptet om text redan finns.
          </Alert>
        )}

        {analysis && proposed && (
          <Paper
            elevation={0}
            sx={{ p: { xs: 1.5, md: 2.5 }, mb: readOnlySnapshot ? 2 : 10, borderRadius: 2.5, border: '1px solid rgba(7,47,83,0.08)', boxShadow: '0 8px 24px rgba(7,47,83,0.05)' }}
          >
            {analysis.overflowWarnings.map((warning) => (
              <Alert key={warning} severity="warning" sx={{ mb: 1.5 }}>{warning}</Alert>
            ))}

            {readOnlySnapshot && analysis.changes.length === 0 && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                AI-motiveringarna sparades inte för den här äldre körningen. Fältdiffarna nedan visar vad som ändrades.
              </Alert>
            )}

            {analysis.changes.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY, mb: 1 }}>Vad AI:n ändrade</Typography>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {analysis.changes.map((change, index) => (
                    <Box
                      key={`${change.falt}-${index}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#f8fafc',
                        border: '1px solid rgba(7,47,83,0.08)',
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>{change.falt || 'Fält'}</Typography>
                      {change.anledning && (
                        <Typography variant="body2" sx={{ color: '#334155', mb: 1 }}>{change.anledning}</Typography>
                      )}
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                        <Box>
                          <ColumnLabel tone="original">Borttaget</ColumnLabel>
                          <OriginalDiffText original={change.fore || ''} proposed={change.efter || ''} />
                        </Box>
                        <Box>
                          <ColumnLabel tone="proposed">Tillagt</ColumnLabel>
                          <ProposedDiffText original={change.fore || ''} proposed={change.efter || ''} />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY }}>Granska fält</Typography>
            <Typography sx={{ color: '#334155', mt: 0.5, mb: 1.5, maxWidth: 820 }}>
              {readOnlySnapshot
                ? 'Så här såg granskningen ut när receptet godkändes. Grön bock visar vilka fält som skrevs in.'
                : 'Grön bock betyder att förslaget skrivs in i receptet. Ta bort bocken om originaltexten ska behållas. Oförändrade fält är obockade från start. Ingredienser och steg som inte längre behövs töms automatiskt.'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
              <Chip
                size="small"
                label="Röd överstruken = borttagen text"
                sx={{ bgcolor: 'rgba(180,35,24,0.08)', color: '#b42318', fontWeight: 700 }}
              />
              <Chip
                size="small"
                label="Grön fetstil = ny text"
                sx={{ bgcolor: 'rgba(21,115,71,0.10)', color: '#157347', fontWeight: 700 }}
              />
              <Chip
                size="small"
                label="Grön bock = sparas"
                sx={{ bgcolor: 'rgba(24,134,74,0.10)', color: CHECK_GREEN, fontWeight: 700 }}
              />
            </Box>

            {SCALAR_FIELDS.map((field) => {
              const originalValue = analysis.original[field.key] || '';
              const proposedValue = proposed[field.key] || '';
              const changed = originalValue.trim() !== proposedValue.trim();
              const accepted = acceptedScalars.has(field.key);
              return (
                <ReviewCard key={field.key} accepted={accepted} changed={changed}>
                  <ApproveCheck
                    checked={accepted}
                    disabled={readOnlySnapshot || !changed}
                    readOnly={readOnlySnapshot}
                    label={`Spara ${field.label}`}
                    onChange={(checked) => {
                      setAcceptedScalars((current) => {
                        const next = new Set(current);
                        if (checked) next.add(field.key);
                        else next.delete(field.key);
                        return next;
                      });
                    }}
                  />
                  <Box>
                    <ColumnLabel tone="original">{field.label} · original</ColumnLabel>
                    <Box sx={{ px: 1.25, py: 1, borderRadius: 1.5, bgcolor: '#fff7f6', border: '1px solid rgba(180,35,24,0.12)' }}>
                      <OriginalDiffText original={originalValue} proposed={proposedValue} />
                    </Box>
                  </Box>
                  <ProposedField
                    originalValue={originalValue}
                    proposedValue={proposedValue}
                    onEdit={(value) => updateProposedScalar(field.key, value)}
                    multiline={field.multiline}
                    readOnly={readOnlySnapshot}
                    editing={editingKey === field.key}
                    onToggleEditing={() => setEditingKey(editingKey === field.key ? null : field.key)}
                  />
                </ReviewCard>
              );
            })}

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.25, fontWeight: 800, color: NAVY }}>Ingredienser</Typography>
            {proposed.ingredienser.map((value, index) => {
              const originalValue = analysis.original.ingredienser[index] || '';
              const changed = originalValue.trim() !== value.trim();
              const accepted = acceptedIngredients.has(index);
              return (
                <ReviewCard key={`ing-${index}`} accepted={accepted} changed={changed}>
                  <ApproveCheck
                    checked={accepted}
                    disabled={readOnlySnapshot || !changed}
                    readOnly={readOnlySnapshot}
                    label={`Spara ingrediens ${index + 1}`}
                    onChange={(checked) => {
                      setAcceptedIngredients((current) => {
                        const next = new Set(current);
                        if (checked) next.add(index);
                        else next.delete(index);
                        return next;
                      });
                    }}
                  />
                  <Box>
                    <ColumnLabel tone="original">Ingrediens {index + 1} · original</ColumnLabel>
                    <Box sx={{ px: 1.25, py: 1, borderRadius: 1.5, bgcolor: '#fff7f6', border: '1px solid rgba(180,35,24,0.12)' }}>
                      <OriginalDiffText original={originalValue} proposed={value} />
                    </Box>
                  </Box>
                  <ProposedField
                    originalValue={originalValue}
                    proposedValue={value}
                    onEdit={(next) => updateProposedList('ingredienser', index, next)}
                    readOnly={readOnlySnapshot}
                    editing={editingKey === `ing-${index}`}
                    onToggleEditing={() => setEditingKey(editingKey === `ing-${index}` ? null : `ing-${index}`)}
                  />
                </ReviewCard>
              );
            })}
            {leftoverIngredients.map((entry) => (
              <ReviewCard key={`ing-left-${entry.index}`} accepted={false} changed>
                <Box />
                <Box>
                  <ColumnLabel tone="original">Ingrediens {entry.index + 1} töms</ColumnLabel>
                  <Box sx={{ px: 1.25, py: 1, borderRadius: 1.5, bgcolor: '#fff7f6', border: '1px solid rgba(180,35,24,0.12)' }}>
                    <OriginalDiffText original={entry.original} proposed="" />
                  </Box>
                </Box>
                <Box>
                  <ColumnLabel tone="proposed">Förslag</ColumnLabel>
                  <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>Töms automatiskt</Typography>
                </Box>
              </ReviewCard>
            ))}

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.25, fontWeight: 800, color: NAVY }}>Instruktioner</Typography>
            {proposed.instruktioner.map((value, index) => {
              const originalValue = analysis.original.instruktioner[index] || '';
              const changed = originalValue.trim() !== value.trim();
              const accepted = acceptedSteps.has(index);
              return (
                <ReviewCard key={`steg-${index}`} accepted={accepted} changed={changed}>
                  <ApproveCheck
                    checked={accepted}
                    disabled={readOnlySnapshot || !changed}
                    readOnly={readOnlySnapshot}
                    label={`Spara steg ${index + 1}`}
                    onChange={(checked) => {
                      setAcceptedSteps((current) => {
                        const next = new Set(current);
                        if (checked) next.add(index);
                        else next.delete(index);
                        return next;
                      });
                    }}
                  />
                  <Box>
                    <ColumnLabel tone="original">Steg {index + 1} · original</ColumnLabel>
                    <Box sx={{ px: 1.25, py: 1, borderRadius: 1.5, bgcolor: '#fff7f6', border: '1px solid rgba(180,35,24,0.12)' }}>
                      <OriginalDiffText original={originalValue} proposed={value} />
                    </Box>
                  </Box>
                  <ProposedField
                    originalValue={originalValue}
                    proposedValue={value}
                    onEdit={(next) => updateProposedList('instruktioner', index, next)}
                    multiline
                    readOnly={readOnlySnapshot}
                    editing={editingKey === `steg-${index}`}
                    onToggleEditing={() => setEditingKey(editingKey === `steg-${index}` ? null : `steg-${index}`)}
                  />
                </ReviewCard>
              );
            })}
            {leftoverSteps.map((entry) => (
              <ReviewCard key={`steg-left-${entry.index}`} accepted={false} changed>
                <Box />
                <Box>
                  <ColumnLabel tone="original">Steg {entry.index + 1} töms</ColumnLabel>
                  <Box sx={{ px: 1.25, py: 1, borderRadius: 1.5, bgcolor: '#fff7f6', border: '1px solid rgba(180,35,24,0.12)' }}>
                    <OriginalDiffText original={entry.original} proposed="" />
                  </Box>
                </Box>
                <Box>
                  <ColumnLabel tone="proposed">Förslag</ColumnLabel>
                  <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>Töms automatiskt</Typography>
                </Box>
              </ReviewCard>
            ))}
          </Paper>
        )}
      </Box>

      {analysis && proposed && !readOnlySnapshot && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            borderTop: '1px solid rgba(7,47,83,0.1)',
            bgcolor: 'rgba(255,255,255,0.94)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2, md: 3 },
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#475569', mr: 'auto' }}>
            {acceptedCount} {acceptedCount === 1 ? 'ändring' : 'ändringar'} med grön bock
            {leftoverCount > 0 ? ` · ${leftoverCount} rader töms` : ''}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => void handleApprove()}
            disabled={!canApprove}
            startIcon={<CheckCircleIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: CHECK_GREEN, px: 3, '&:hover': { bgcolor: '#146c3c' } }}
          >
            {saving ? 'Sparar...' : 'Godkänn receptet'}
          </Button>
        </Box>
      )}

      <Dialog open={promptOpen} onClose={() => setPromptOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: NAVY }}>Masterprompt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {readOnlySnapshot
              ? 'Skrivreglerna som användes när receptet godkändes.'
              : 'Skrivreglerna som ChatGPT följer. Använd rubriker, fetstil och listor.'}
          </Typography>
          <PromptRichEditor
            value={promptDraft}
            sourceKey={`${prompt?.updatedAt || 'init'}:${(prompt?.systemPrompt || '').length}:${promptOpen ? 'open' : 'closed'}`}
            onChange={setPromptDraft}
            editable={Boolean(session?.canEditPrompt) && !readOnlySnapshot}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {readOnlySnapshot
              ? 'Masterprompten visas som den såg ut när receptet godkändes.'
              : session?.canEditPrompt
                ? 'Formateringen sparas som markdown så att både redigering och ChatGPT blir läsbara.'
                : 'Du kan läsa reglerna men inte ändra dem.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!readOnlySnapshot && session?.canEditPrompt && (
            <Button
              variant="contained"
              onClick={() => void handleSavePrompt()}
              disabled={savingPrompt}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: NAVY, '&:hover': { bgcolor: '#0a4175' } }}
            >
              {savingPrompt ? 'Sparar...' : 'Spara masterprompt'}
            </Button>
          )}
          <Button onClick={() => setPromptOpen(false)} sx={{ textTransform: 'none' }}>Stäng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importConfirmOpen} onClose={() => setImportConfirmOpen(false)}>
        <DialogTitle>Fälten är inte tomma</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Receptet har redan text. Importen kan skriva över befintliga fält. Vill du fortsätta?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportConfirmOpen(false); setPendingFile(null); }}>Avbryt</Button>
          <Button variant="contained" onClick={() => pendingFile && void runImport(pendingFile)}>Importera nytt recept</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecipeCorrectionPage;
